/**
 * useMonthlyUsage — real-time listener on /clients/{id}/monthlyCounts/{YYYY-MM}
 *
 * Returns the current month's call/contact count, the client's limit (with
 * defaults), and a percentage. Used by the Dashboard stat strip and the
 * dispatcher's pre-send guard.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const DEFAULT_CALL_LIMIT = 30000;
const DEFAULT_CONTACT_LIMIT = 1200;

interface MonthlyUsage {
  used: number;
  limit: number;
  remaining: number;
  percent: number;       // 0-100+
  monthKey: string;       // YYYY-MM
  loading: boolean;
}

function currentMonthKey(): string {
  // Match the Firebase function's CST month key
  const now = new Date();
  const cst = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const y = cst.getFullYear();
  const m = String(cst.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function useMonthlyUsage(
  clientId: string | null,
  billingType: 'per_call' | 'per_contact'
): MonthlyUsage {
  const [usage, setUsage] = useState<MonthlyUsage>({
    used: 0,
    limit: billingType === 'per_contact' ? DEFAULT_CONTACT_LIMIT : DEFAULT_CALL_LIMIT,
    remaining: 0,
    percent: 0,
    monthKey: currentMonthKey(),
    loading: true,
  });

  useEffect(() => {
    if (!clientId) return;

    const monthKey = currentMonthKey();
    const usageField = billingType === 'per_contact' ? 'contactsUsed' : 'callsUsed';

    // Listen to BOTH the client doc (for the limit) and the monthly count doc (for usage)
    let currentUsed = 0;
    let currentLimit = billingType === 'per_contact' ? DEFAULT_CONTACT_LIMIT : DEFAULT_CALL_LIMIT;

    const update = () => {
      const remaining = Math.max(0, currentLimit - currentUsed);
      const percent = currentLimit > 0 ? Math.round((currentUsed / currentLimit) * 100) : 0;
      setUsage({
        used: currentUsed,
        limit: currentLimit,
        remaining,
        percent,
        monthKey,
        loading: false,
      });
    };

    const clientUnsub = onSnapshot(doc(db, 'clients', clientId), snap => {
      if (snap.exists()) {
        const d = snap.data();
        currentLimit = (billingType === 'per_contact'
          ? (d.monthlyContactLimit as number)
          : (d.monthlyCallLimit as number)) || (billingType === 'per_contact' ? DEFAULT_CONTACT_LIMIT : DEFAULT_CALL_LIMIT);
        update();
      }
    });

    const monthUnsub = onSnapshot(
      doc(db, 'clients', clientId, 'monthlyCounts', monthKey),
      snap => {
        currentUsed = snap.exists() ? ((snap.data()?.[usageField] as number) || 0) : 0;
        update();
      }
    );

    return () => {
      clientUnsub();
      monthUnsub();
    };
  }, [clientId, billingType]);

  return usage;
}
